/**
 * Web wrapper around zen-core-elements' ZenDropdown.
 *
 * The problem: an open dropdown list is painted *under* the form content that
 * follows it. The country-code picker on Add Customer shows it plainly - the
 * "Country code is required" message and the "Notes" label sit on top of the
 * option rows.
 *
 * It is not the library's fault. ZenDropdown already positions its list
 * absolutely and gives it `zIndex: 5000`. The cause is one line in
 * react-native-web's View:
 *
 *   react-native-web/dist/exports/View/index.js:134   zIndex: 0
 *
 * Every View is emitted as `position: relative; z-index: 0`, and a positioned
 * element with a z-index is a *stacking context*. So every View in the tree
 * traps its descendants' z-index: the list's 5000 can never rise past the
 * <View> that holds it, and because every sibling View is also z-index 0, the
 * painting order between them falls back to document order. Whatever markup
 * comes after the dropdown wins, no matter how large its z-index is.
 *
 * Measured on the customer form: nine ancestors in a row, every one of them
 * `position: relative, z-index: 0`.
 *
 * That also explains why the screens carry `Platform.OS == "ios" && {zIndex: 10}`
 * guards in a dozen places - iOS needed lifting, Android did not, and web was
 * never in that expression at all.
 *
 * The fix has two halves, and neither touches the shared component:
 *
 *   1. this wrapper marks its outermost element while the dropdown is open, and
 *   2. globalStyles.web.ts lifts *every* ancestor of that mark, which is the
 *      only way to get a descendant out of a chain of stacking contexts.
 *
 * Metro never resolves this file, so Android and the tablets keep the exact
 * behaviour they have today.
 */

import React from 'react';
import {View} from 'react-native';

import BaseZenDropdown, {
  ValueType,
  ZenDropdownInterface,
} from 'zen-core-elements/src/elements/zenDropdown/dropdownPicker';

/**
 * `dataSet` is a react-native-web extension that renders data-* attributes;
 * react-native's own prop types do not declare it, hence the cast. The value
 * is read by globalStyles.web.ts and by nothing else.
 */
const OPEN_MARKER = {dataSet: {zenDropdownOpen: 'true'}} as object;

const WebZenDropdown = ({
  mainContainerStyle,
  ...props
}: ZenDropdownInterface<ValueType>) => (
  /*
   * The caller's mainContainerStyle moves out to this wrapper rather than
   * staying on the component's own root. All four call sites that use it pass
   * a `flex` or a `width`, which has to live on the outermost element for the
   * surrounding row to honour it. The inner root then stretches to this
   * wrapper, so the rendered result is the same box it was before.
   */
  <View {...(props.open ? OPEN_MARKER : {})} style={mainContainerStyle}>
    <BaseZenDropdown {...(props as ZenDropdownInterface<ValueType>)} />
  </View>
);

export default WebZenDropdown;
